'use client';

import path from 'path';
import React, { useContext } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { GlobeIcon } from '@phosphor-icons/react';
import { IconButton, Menu, MenuItem } from '@unity/cloud-ui';
import { Locale, usePathname, routing } from '@/i18n/routing';
import { dataLayerPush, EVENT_USER_EVENT } from '@/lib/data-layer-push';
import { LocaleAvailabilityContext } from '@/i18n/locale-availability-provider';
import { SUPPORTED_LOCALES } from '@/i18n';
import { getLocation } from '@/utils/get-location';

export const LocaleSwitcher = () => {
  const { defaultLocale } = routing;
  const currentLocale = useLocale();
  const t = useTranslations();
  const pathname = usePathname();
  const [languageAnchorEl, setLanguageAnchorEl] = React.useState<null | HTMLElement>(null);
  const languageOpen = Boolean(languageAnchorEl);

  const handleOnChange = (nextLocale: Locale): void => {
    if (nextLocale === currentLocale) {
      return;
    }

    dataLayerPush({
      event: EVENT_USER_EVENT,
      event_name: 'ui_action',
      properties: {
        ui_action: 'locale_switch',
        ui_locale_switch: nextLocale,
      },
    });

    const currentLocation = getLocation();
    const searchParams = currentLocation.search;
    const hash = currentLocation.hash;

    let newPathname: string;
    if (pathname.includes(`/${currentLocale}/`)) {
      newPathname = pathname.replace(`/${currentLocale}/`, `/${nextLocale}/`);
    } else {
      newPathname = path.join('/', nextLocale, pathname);
    }

    currentLocation.assign(newPathname + searchParams + hash);
  };

  const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setLanguageAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setLanguageAnchorEl(null);
  };

  // the available locales will change when the user navigates to the
  // namespace for a different documentation set:
  const { locales: availableLocales } = useContext(LocaleAvailabilityContext);

  return (
    <>
      <IconButton
        color="default"
        id="language-button"
        aria-controls={languageOpen ? 'language-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={languageOpen ? 'true' : undefined}
        aria-label={t('header:locale-switcher:aria-label')}
        onClick={handleButtonClick}
      >
        <GlobeIcon size={20} data-testid="localization-icon" />
      </IconButton>
      <Menu
        disableScrollLock
        open={languageOpen}
        defaultValue={defaultLocale}
        onClose={handleMenuClose}
        anchorEl={languageAnchorEl}
        MenuListProps={{
          'aria-labelledby': 'language-button',
          disablePadding: false,
        }}
      >
        {availableLocales.map((locale: SUPPORTED_LOCALES) => (
          <MenuItem key={locale} selected={locale === currentLocale} onClick={() => handleOnChange(locale)}>
            {t(`header:locale-switcher:${locale}`)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
